import React, { useState } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useTenant } from '../contexts/TenantContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { resolveProductEntry } from '../services/productEntry';
import { useLanguage, switchLang } from '../i18n';

const INTERNAL_ADMIN_ROLES = new Set(['superadmin', 'ops', 'support', 'finance']);
const BRAND_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/bizsprite-logo.png`;

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '15px',
  color: '#1A202C',
  background: '#FFFFFF',
  border: '1px solid #CBD5E0',
  borderRadius: '7px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s ease',
};

const labelStyle = {
  display: 'block',
  fontSize: '14px',
  fontWeight: 600,
  color: '#2D3748',
  marginBottom: '6px',
};

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const { login } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useLanguage();

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result.success) {
        const returnTo = location.state?.ecommerceEntryReturn;
        if (typeof returnTo === 'string' && returnTo.startsWith('/ecommerce-entry?')) {
          navigate(returnTo, { replace: true });
          return;
        }
        if (!INTERNAL_ADMIN_ROLES.has((result.role || '').trim().toLowerCase())) {
          const productEntry = await resolveProductEntry();
          if (productEntry.legacy && productEntry.product === 'ecommerce') { window.location.replace(productEntry.entry_url); return; }
          if (productEntry.product === 'ecommerce') { navigate(productEntry.entry_url, { replace: true }); return; }
        }

        if (INTERNAL_ADMIN_ROLES.has((result.role || '').trim().toLowerCase())) {
          window.location.href = '/admin/';
        } else {
          navigate('/app/settings', { replace: true });
        }
      }
    } catch (error) {
      if (error.code === 'tenant_suspended') {
        setErrorMsg(error.message || 'Your account has been suspended. Please contact support.');
      } else {
        setErrorMsg(error.message || 'Invalid username or password.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F5F7FA',
        padding: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={switchLang}
        style={{
          position: 'absolute',
          top: '20px',
          right: '24px',
          background: 'none',
          border: 'none',
          fontSize: '14px',
          color: '#5B6575',
          cursor: 'pointer',
          padding: '12px 8px',
        }}
      >
        {t('lang_label')}
      </button>
      <div
        style={{
          width: '100%',
          maxWidth: '420px',
          background: '#FFFFFF',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(23,32,51,0.06)',
          padding: '40px 36px',
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: '28px', textAlign: 'center' }}>
          <img
            src={BRAND_LOGO_SRC}
            alt="Bizsprite"
            style={{
              width: '170px',
              height: '52px',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto 16px',
              background: '#FFFFFF',
            }}
          />
          <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1A202C' }}>
            {t('login_title')}
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: '14px', color: '#4A5568' }}>
            {t('login_subtitle')}
          </p>
        </div>

        {new URLSearchParams(location.search).get('reason') === 'session-invalid' && (
          <p role="status" style={{ color: '#4A5568', marginBottom: '20px', lineHeight: 1.5 }}>
            {lang === 'zh'
              ? '登录状态已失效，请重新登录。如果从 OneSuite 进入，请返回 OneSuite 重新打开 Bizsprite。'
              : 'Your sign-in is no longer valid. Please sign in again. If you use OneSuite, return there and open Bizsprite again.'}
          </p>
        )}

        {/* Error */}
        {errorMsg && (
          <div
            style={{
              background: '#FFF5F5',
              border: '1px solid #FC8181',
              borderRadius: '7px',
              padding: '10px 14px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#C53030',
            }}
          >
            {errorMsg}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: '18px' }}>
            <label style={labelStyle} htmlFor="login-username">
              {t('login_username')} <span style={{ color: '#E53E3E' }}>*</span>
            </label>
            <input
              id="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder={t('login_username_placeholder')}
              autoFocus
              autoComplete="username"
              required
              style={inputStyle}
              onFocus={(e) => { e.target.style.borderColor = '#2563EB'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.20)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#CBD5E0'; e.target.style.boxShadow = 'none'; }}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={labelStyle} htmlFor="login-password">
              {t('login_password')} <span style={{ color: '#E53E3E' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="login-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                style={{ ...inputStyle, paddingRight: '44px' }}
                onFocus={(e) => { e.target.style.borderColor = '#2563EB'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,0.20)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#CBD5E0'; e.target.style.boxShadow = 'none'; }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#5B6575',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                }}
                aria-label={showPassword ? t('login_hide_password') : t('login_show_password')}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '11px',
              background: loading ? '#90CDF4' : '#2563EB',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '7px',
              fontSize: '15px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => { if (!loading) e.target.style.background = '#2B6CB0'; }}
            onMouseLeave={(e) => { if (!loading) e.target.style.background = '#2563EB'; }}
          >
            {loading ? t('login_submitting') : t('login_submit')}
          </button>
        </form>

        {/* Footer */}
        <p style={{ marginTop: '20px', fontSize: '12px', color: '#5B6575', textAlign: 'center' }}>
          {t('login_terms_prefix')}{' '}
          <RouterLink to="/terms" style={{ color: '#2563EB', textDecoration: 'none' }}>{t('login_terms')}</RouterLink>
          {' '}{t('login_and')}{' '}
          <RouterLink to="/privacy-policy" style={{ color: '#2563EB', textDecoration: 'none' }}>{t('login_privacy')}</RouterLink>.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
